import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { MapPin, ChevronRight, RefreshCcw, Cloud } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { triggerWeatherExtremeCheck } from '../lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'bestbuddy_geo';

/**
 * Dashboard "Today's Weather Brief" widget.
 *
 * Compact 3-line snapshot: top tip for nutrition, fitness, and sleep — each
 * row is tappable and deep-links to the corresponding page where the full
 * WeatherTipsCard lives. Designed to be the "discovery hook" on the Dashboard
 * so users don't miss the weather-aware tips on the dedicated tabs.
 *
 * Zero LLM cost — rule-based backend.
 */
export const WeatherBriefCard = () => {
    const { language } = useLanguage();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [weather, setWeather] = useState(null);
    const [items, setItems] = useState([]);
    const [error, setError] = useState(null);
    const [needsPermission, setNeedsPermission] = useState(false);

    const isDe = language === 'de';

    const readCachedCoords = () => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            const age = Date.now() - (parsed.ts || 0);
            if (age > 12 * 60 * 60 * 1000) return null;
            return parsed.coords || null;
        } catch { return null; }
    };

    const cacheCoords = (coords) => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ ts: Date.now(), coords }));
        } catch { /* ignore */ }
    };

    const fetchBrief = useCallback(async (coords) => {
        setLoading(true); setError(null);
        try {
            const { data } = await axios.get(`${API}/weather/brief`, {
                params: { lat: coords.lat, lon: coords.lon, language },
            });
            if (data?.error) {
                setError(isDe ? 'Wetter nicht erreichbar' : 'Weather unavailable');
                setItems([]); setWeather(null);
            } else {
                setWeather(data.weather);
                setItems(data.items || []);
                // Fire-and-forget: ask backend to send a push if today's weather
                // is extreme and we haven't pushed yet today. Idempotent per UTC-day.
                triggerWeatherExtremeCheck(coords.lat, coords.lon, language).catch(() => { /* ignore */ });
            }
        } catch {
            setError(isDe ? 'Brief konnte nicht geladen werden' : 'Brief could not load');
        } finally {
            setLoading(false);
        }
    }, [language, isDe]);

    const requestLocation = useCallback(() => {
        if (!navigator.geolocation) {
            setError(isDe ? 'Browser unterstützt keinen Standort' : 'Browser does not support geolocation');
            setLoading(false);
            return;
        }
        setNeedsPermission(false);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const coords = { lat: pos.coords.latitude, lon: pos.coords.longitude };
                cacheCoords(coords);
                fetchBrief(coords);
            },
            (err) => {
                setLoading(false);
                if (err.code === 1) setNeedsPermission(true);
                else setError(isDe ? 'Standort konnte nicht ermittelt werden' : 'Location unavailable');
            },
            { enableHighAccuracy: false, timeout: 7000, maximumAge: 60 * 60 * 1000 }
        );
    }, [fetchBrief, isDe]);

    useEffect(() => {
        const cached = readCachedCoords();
        if (cached) fetchBrief(cached);
        else requestLocation();
    }, [fetchBrief, requestLocation]);

    if (loading) {
        return (
            <Card data-testid="weather-brief">
                <CardContent className="p-4 flex items-center gap-3 text-sm text-muted-foreground">
                    <Cloud className="w-5 h-5 animate-pulse" />
                    {isDe ? 'Wetter-Briefing wird geladen...' : 'Loading weather brief...'}
                </CardContent>
            </Card>
        );
    }

    if (needsPermission) {
        return (
            <Card data-testid="weather-brief">
                <CardContent className="p-4 flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm font-semibold">
                            {isDe ? 'Standort für tägliches Wetter-Briefing freigeben' : 'Allow location for daily weather brief'}
                        </p>
                        <Button size="sm" className="mt-2" onClick={requestLocation} data-testid="weather-brief-permission-btn">
                            {isDe ? 'Standort freigeben' : 'Allow location'}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (error || !items.length) return null;

    const peakToday = weather?.peak_today ?? weather?.temperature;
    const heatingUp = weather?.is_heating_up;
    const isHeat = peakToday >= 28;
    const isCold = peakToday < 5;
    const headerTone = isHeat
        ? 'from-orange-500/20 via-amber-500/15 to-red-500/15 border-orange-200'
        : isCold
            ? 'from-sky-500/20 via-cyan-500/15 to-indigo-500/15 border-sky-200'
            : 'from-emerald-500/15 via-teal-500/10 to-cyan-500/10 border-emerald-200';

    const headline = heatingUp
        ? (isDe ? `Hitze kommt: ${weather.temperature}° → ${weather.day_max}°C` : `Heat coming: ${weather.temperature}° → ${weather.day_max}°C`)
        : isHeat
            ? (isDe ? `Hitze-Tag · ${peakToday}°C` : `Heat day · ${peakToday}°C`)
            : isCold
                ? (isDe ? `Kalt heute · ${peakToday}°C` : `Cold today · ${peakToday}°C`)
                : (isDe ? `${weather.temperature}°C · ${weather.label}` : `${weather.temperature}°C · ${weather.label}`);

    return (
        <Card data-testid="weather-brief" className="overflow-hidden">
            <div className={`bg-gradient-to-br ${headerTone} border-b px-4 py-3 flex items-center gap-3`}>
                <div className="text-3xl" aria-hidden="true">{weather?.emoji}</div>
                <div className="flex-1 min-w-0">
                    <p className="text-[11px] uppercase tracking-wide font-bold text-muted-foreground">
                        {isDe ? 'Nanouks Tages-Briefing' : "Nanouk's daily brief"}
                    </p>
                    <p className="text-base font-bold leading-tight" data-testid="weather-brief-headline">{headline}</p>
                </div>
                <Button size="icon" variant="ghost" onClick={requestLocation} data-testid="weather-brief-refresh" aria-label="Refresh">
                    <RefreshCcw className="w-4 h-4" />
                </Button>
            </div>
            <CardContent className="p-0 divide-y">
                {items.map((item) => {
                    const catLabel = item.category === 'nutrition'
                        ? (isDe ? 'Ernährung' : 'Nutrition')
                        : item.category === 'fitness'
                            ? (isDe ? 'Training' : 'Fitness')
                            : (isDe ? 'Schlaf' : 'Sleep');
                    return (
                        <button
                            key={item.category}
                            type="button"
                            onClick={() => navigate(item.route)}
                            className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors active:bg-muted"
                            data-testid={`weather-brief-${item.category}`}
                        >
                            <div className="text-xl flex-shrink-0">{item.icon}</div>
                            <div className="flex-1 min-w-0">
                                <p className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">{catLabel}</p>
                                <p className="text-sm font-semibold leading-tight flex items-center gap-1.5">
                                    {item.title}
                                    {item.severity === 'high' && (
                                        <span className="text-[9px] uppercase tracking-wide font-bold text-red-600 bg-red-50 border border-red-200 rounded px-1 py-0.5">
                                            {isDe ? 'Wichtig' : 'Important'}
                                        </span>
                                    )}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1 leading-snug line-clamp-2">{item.body}</p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-2" />
                        </button>
                    );
                })}
            </CardContent>
        </Card>
    );
};
